import { DatePicker, DatePickerPopover, Dot } from "@xola/ui-kit";
import clsx from "clsx";
import { DateFormat, formatDate, getUnitsPrice, isBefore } from "common";
import dayjs, { Dayjs } from "dayjs";
import { sumBy } from "lodash";
import React, { memo, ReactNode, useMemo } from "react";
import { DatepickerPreference, DisplayType, Timeslot, TimeslotUnit } from "types";
import { Currency } from "../Common/Currency";
import { NoTranslate } from "../Common/NoTranslate";
import { isSlotUnavailable, UnavailabilityMessagesByDate } from "./ArrivalPickerHelper";
import { getOpenCapacity } from "@/models/Timeslot";
import { useLocalizationStore } from "@/stores/localizationStore";

export type BookableDates = Record<string, Timeslot[]>;

interface ArrivalDatePickerProps {
    month: Dayjs;
    bookableDates: BookableDates;
    unavailabilityMessagesByDate?: UnavailabilityMessagesByDate;
    components?: { Footer?: ReactNode };
    waitlistDates?: Dayjs[];
    value?: string;
    upcomingDates?: Date[];
    placement?: string;
    popover?: boolean;
    datePickerPreference: DatepickerPreference;
    lastScheduledDate?: string;
    onMonthChange: (value: Date) => void;
    disabledDays: (date: Date) => boolean;
    loadingDays?: (date: Date) => boolean;
    onChange: (value: Date, options: { disabled: boolean }) => void;
}

const defaultLoadingDays = (): boolean => {
    return false;
};

export const ArrivalDatePicker = ({
    value,
    month,
    disabledDays,
    loadingDays = defaultLoadingDays,
    bookableDates,
    unavailabilityMessagesByDate = {},
    components,
    waitlistDates,
    upcomingDates,
    placement = "auto",
    popover = true,
    datePickerPreference,
    lastScheduledDate,
    onMonthChange,
    onChange,
}: ArrivalDatePickerProps) => {
    const locale = useLocalizationStore().getLocale();
    // When converting to Date we'll get a time offset to our local time, so convert the date before sending it
    const dateValue = useMemo(() => {
        if (!value) return undefined;
        return dayjs(value).tz().toDate(); // TODO: "now()" won't be appropriate, check with new Date()
    }, [value]);

    const isDatePreferenceNone = datePickerPreference.displayType === DisplayType.NONE;

    const dayContent: { [key: string]: number } = useMemo(() => {
        if (isDatePreferenceNone) {
            return {};
        }

        return getBookableDayContent(bookableDates, datePickerPreference);
    }, [bookableDates, isDatePreferenceNone, datePickerPreference]);

    const handleChange = (value: Date, options: { disabled: boolean }) => onChange(value, options);

    const getDayContent = (day: number, date: Date) => {
        if (isDatePreferenceNone) {
            return;
        }

        if (waitlistDates && canWaitlist(date, dayContent, waitlistDates)) {
            return <Dot color="warning" className="h-2 w-2" />;
        }

        if (isSlotUnavailable(date, bookableDates)) {
            return <Dot color="danger" className="h-2 w-2" />;
        }

        const dateString = formatDate(date);
        if (dayContent[dateString]) {
            const value = dayContent[dateString];
            return <DayContentData value={value} isCount={datePickerPreference.displayType === DisplayType.COUNT} />;
        }

        return null;
    };

    return (
        <div>
            {popover ? (
                <DatePickerPopover
                    shouldShowYearPicker
                    value={dateValue}
                    // We need to format the date to the date-time format before converting to the js Date,
                    // because when converting "month" to js Date, we getting time shifted due to timezone offset in "month".
                    month={dayjs(month, DateFormat.DATE_TIME, locale).toDate()}
                    disabledDays={disabledDays}
                    loadingDays={loadingDays}
                    getDayContent={isDatePreferenceNone ? null : getDayContent}
                    popoverProps={{ appendTo: document.body, zIndex: 40, placement }}
                    components={components}
                    getTooltip={(date: Dayjs) => {
                        return getTooltip(
                            datePickerPreference,
                            bookableDates,
                            unavailabilityMessagesByDate,
                            date,
                            lastScheduledDate,
                        );
                    }}
                    upcomingDates={upcomingDates}
                    onChange={handleChange}
                    onMonthChange={onMonthChange}
                />
            ) : (
                <DatePicker
                    getDayContent={isDatePreferenceNone ? null : getDayContent}
                    disabledDays={disabledDays}
                    loadingDays={loadingDays}
                    getTooltip={(date: Dayjs) => {
                        return getTooltip(
                            datePickerPreference,
                            bookableDates,
                            unavailabilityMessagesByDate,
                            date,
                            lastScheduledDate,
                        );
                    }}
                    value={dateValue}
                    onChange={handleChange}
                />
            )}
        </div>
    );
};

const DayContentData = memo(({ value, isCount }: { value: number; isCount: boolean }) => {
    return (
        <div className="flex items-center justify-center">
            {isCount ? (
                <>
                    <Dot color={clsx(value > 0 ? "success" : "danger")} className="h-2 w-2" />
                    <NoTranslate className="ml-0.5">{value > 99 ? `99+` : value}</NoTranslate>
                </>
            ) : (
                <div data-testid="arrival-date-picker-button" className="flex justify-center">
                    <Currency shouldRemoveTrailingZeroes={false}>{value}</Currency>
                </div>
            )}
        </div>
    );
});

const canWaitlist = (date: Dayjs | Date, dayContent: { [x: string]: number }, waitlistDates: Dayjs[]) => {
    if (waitlistDates.length === 0) {
        return false;
    }

    const dateString = formatDate(date);
    // if waitlist available for that date and availability count is zero
    return waitlistDates.some(
        (waitlistDate) => formatDate(waitlistDate) === dateString && !(dayContent[dateString] > 0),
    );
};

const removeBlankLines = (text: string) => text.replace(/\n{2,}/g, "\n").trim();

const getTooltip = (
    preference: DatepickerPreference,
    bookableDates: BookableDates,
    unavailabilityMessagesByDate: UnavailabilityMessagesByDate,
    date: Dayjs,
    lastDate?: string,
) => {
    if (isSlotUnavailable(date, bookableDates)) {
        const messages = unavailabilityMessagesByDate[formatDate(date)] ?? [];
        if (messages.length === 1) {
            return <NoTranslate className="whitespace-pre-line">{messages[0]}</NoTranslate>;
        }

        if (messages.length > 1) {
            return (
                <div>
                    <p className="mb-1 font-bold">Unavailable for multiple reasons:</p>
                    <NoTranslate as="ul" className="list-disc space-y-2 pl-4">
                        {messages.map((message) => (
                            <li key={message} className="whitespace-pre-line">
                                {removeBlankLines(message)}
                            </li>
                        ))}
                    </NoTranslate>
                </div>
            );
        }

        if (preference.unavailableMessage) {
            return preference.unavailableMessage;
        }
    }

    if (!lastDate) {
        return null;
    }

    const message = preference.unavailableScheduleMessage;
    return message && isBefore(lastDate, date) ? message : null;
};

const getBookableDayContent = (bookableDates: BookableDates, datepickerPreference: DatepickerPreference) => {
    const dayContent: { [key: string]: number } = {};
    for (const key of Object.keys(bookableDates)) {
        if (datepickerPreference.displayType === DisplayType.COUNT) {
            // getOpenCapacity reports equipment unit counts for equipment slots
            const total = sumBy(bookableDates[key], getOpenCapacity);

            if (total) {
                if (datepickerPreference.displayCountMax) {
                    if (total <= datepickerPreference.displayCountMax) {
                        dayContent[key] = total;
                    }
                } else {
                    dayContent[key] = total;
                }
            }
        } else if (datepickerPreference.displayType === DisplayType.PRICE) {
            let units: TimeslotUnit[] = [];
            for (const bookableDate of bookableDates[key]) {
                units = [...units, ...bookableDate.units];
                const total = sumBy(bookableDates[key], getOpenCapacity);
                if (total > 0) {
                    const price = getUnitsPrice(datepickerPreference.priceDisplay, units)?.price;
                    if (price) {
                        dayContent[key] = price;
                    }
                }
            }
        }
    }

    return dayContent;
};
